import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { markSessionKnown } from '../auth-events';
import { api, call, setCsrfToken } from '../client';
import { queryKeys } from './keys';

export async function fetchMe() {
  const me = await call(api.GET('/v1/auth/me'));
  markSessionKnown(true);
  return me;
}

export type Me = Awaited<ReturnType<typeof fetchMe>>;
export type SessionMe = Extract<Me, { authType: 'session' }>;
export type Tenant = NonNullable<SessionMe['tenant']>;
export type Role = SessionMe['tenants'][number]['role'];

export function usePublicConfig() {
  return useQuery({
    queryKey: queryKeys.publicConfig,
    queryFn: () => call(api.GET('/v1/config/public')),
    staleTime: 5 * 60_000,
  });
}

/** The current session. A 401 here simply means "not logged in". */
export function useMe() {
  return useQuery({ queryKey: queryKeys.me, queryFn: fetchMe, retry: false, staleTime: 60_000 });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      call(api.POST('/v1/auth/login', { body })),
    onSuccess: async () => {
      markSessionKnown(true);
      await qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (everywhere: boolean) =>
      call(api.POST(everywhere ? '/v1/auth/logout-all' : '/v1/auth/logout')),
    onSettled: () => {
      markSessionKnown(false);
      setCsrfToken(undefined);
      qc.clear();
    },
  });
}

export function useSwitchTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) =>
      call(api.POST('/v1/auth/switch-tenant', { body: { tenantId } })),
    // Everything cached belongs to the old company.
    onSuccess: () => qc.resetQueries(),
  });
}

export function useSignup() {
  return useMutation({
    mutationFn: (body: { name: string; email: string; password: string; companyName: string }) =>
      call(api.POST('/v1/auth/signup', { body })),
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) => call(api.POST('/v1/auth/verify-email', { body: { token } })),
  });
}

export function useResendVerification() {
  return useMutation({
    mutationFn: (email: string) =>
      call(api.POST('/v1/auth/resend-verification', { body: { email } })),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => call(api.POST('/v1/auth/forgot-password', { body: { email } })),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (body: { token: string; password: string }) =>
      call(api.POST('/v1/auth/reset-password', { body })),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) => call(api.PATCH('/v1/auth/me', { body })),
    onSuccess: (me) => qc.setQueryData(queryKeys.me, me),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      call(api.POST('/v1/auth/change-password', { body })),
  });
}

export function useInvitePreview(token: string) {
  return useQuery({
    queryKey: queryKeys.invitePreview(token),
    queryFn: () => call(api.POST('/v1/invites/preview', { body: { token } })),
    enabled: token.length > 0,
    retry: false,
    staleTime: Infinity,
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { token: string; name?: string; password?: string }) =>
      call(api.POST('/v1/invites/accept', { body })),
    onSuccess: async () => {
      markSessionKnown(true);
      await qc.resetQueries();
    },
  });
}
